/* eslint-disable indent,prettier/prettier */

import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { DefaultTabs } from '@/components/Insights/Tabs/default';
import { OutlineTabs } from '@/components/Insights/Tabs/outline';
import { DEFAULT_TAB_NAME, DEFAULT_SEARCH_PARAMS } from '@/constants/insights';
import Select from 'react-select';
import { ITabOption, TabVariant } from '@/interfaces/layout';
import { TABS_MODE } from '@/constants/navigations';

interface IProps extends PropsWithChildren {
  tabs: ITabOption[];
  variant: TabVariant;
  defaultValue?: ITabOption;
  wrapperClass?: string;
  setPageSearchParams?: boolean;
  tabId?: string;
  lastActiveTab?: boolean;
}

export const Tabs = ({
  tabs,
  variant,
  defaultValue = DEFAULT_TAB_NAME,
  wrapperClass,
  setPageSearchParams = false,
  tabId = 'insights-tabs',
  lastActiveTab = false,
  children,
}: IProps) => {
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(searchParams);
    const categoryFromSearchParams = urlSearchParams.get(DEFAULT_SEARCH_PARAMS.category);

    if (!categoryFromSearchParams || categoryFromSearchParams === defaultValue?.value) {
      setActiveTab(defaultValue);

      return;
    }

    if (categoryFromSearchParams) {
      setActiveTab({
        value: categoryFromSearchParams,
        label: categoryFromSearchParams,
      });
    }
  }, [defaultValue, searchParams, tabs]);

  const handleTabChange = useCallback(
    (tab: { value: string; label: string }) => {
      const urlSearchParams = new URLSearchParams(searchParams);
      setPageSearchParams && urlSearchParams.set(DEFAULT_SEARCH_PARAMS.page, '1');

      if (tab.value === defaultValue?.value) {
        urlSearchParams.delete(DEFAULT_SEARCH_PARAMS.category);
      } else {
        urlSearchParams.set(DEFAULT_SEARCH_PARAMS.category, tab.value);
      }

      router.replace(`${pathname}?${urlSearchParams.toString()}`, {
        scroll: false,
      });
    },
    [setPageSearchParams, defaultValue?.value, pathname, router, searchParams],
  );

  const renderTabs = () => {
    switch (variant) {
      case TABS_MODE.outline:
        return (
          <OutlineTabs
            lastActiveTab={lastActiveTab && !!children ? children : null}
            wrapperClass={wrapperClass}
            tabs={tabs}
            handleTabChange={handleTabChange}
            activeTab={activeTab.value}
            tabId={tabId}
          />
        );

      case TABS_MODE.select:
        return (
          <Select
            data-testid={`${tabId}-select`}
            value={activeTab}
            name="color"
            options={tabs}
            classNames={{
              dropdownIndicator: () => `
                  !transition-none
                  !text-black-6
                `,
              indicatorSeparator: () => 'hidden',
              singleValue: () => `!text-black !m-[0px] !font-bold`,
              control: () => `
                  !rounded-lg !cursor-pointer !text-white !box-border
                  !transition-none !shadow-sm !border-grey-1
                `,
              valueContainer: () => `!h-[43px] !box-border !rounded-full !border-grey-1`,
              option: () =>
                `hover:!bg-purple-7 hover:!text-white active:!bg-purple-7 active:!text-white !bg-white !text-black-10`,
            }}
            onChange={(tab) => handleTabChange(tab as { value: string; label: string })}
          />
        );

      default:
        return (
          <DefaultTabs
            tabs={tabs}
            handleTabChange={handleTabChange}
            activeTab={activeTab.value}
            tabId={tabId}
          />
        );
    }
  };

  return renderTabs();
};
